import React, { useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PasswordInputField from "../../../commons/PasswordInputField";
import IconButton from "../../../iconButton";
import {
  CheckCircleIcon,
  XCircleIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  useChangePasswordMutation,
  useGetProfessionalInfoQuery,
} from "../../../../store/professionalInfo";
import { toast } from "react-toastify";

const AdminChangePassword = ({ isMobile }) => {
  const [
    changePassword,
    {
      isSuccess: changePasswordSuccess,
      isLoading: changePasswordLoader,
      isError: changePasswordError,
      data: responseData,
      error: errorResponse,
    },
  ] = useChangePasswordMutation();
  const { data: professionalInfo } = useGetProfessionalInfoQuery();

  useEffect(() => {
    if (errorResponse?.status === 401) {
      toast.error(errorResponse?.data?.message);
    }
    if (errorResponse?.status === 400) {
      toast.error(errorResponse?.data?.password[0]);
    }

    if (changePasswordSuccess && !changePasswordLoader) {
      toast.success(responseData?.message);
      formik.resetForm();
    }
  }, [changePasswordSuccess, changePasswordError]);

  const formik = useFormik({
    initialValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      oldPassword: Yup.string().required("Existing password is required"),
      newPassword: Yup.string().required("Password is required."),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: (values) => {
      const params = {
        email: professionalInfo?.data?.email,
        old_password: values.oldPassword,
        password: values.newPassword,
        password_confirmation: values.confirmPassword,
      };

      changePassword(params);
    },
  });

  const ValidationMessage = ({ isValid, message }) => {
    return (
      <div className="flex items-center text-sm mt-1">
        {isValid ? (
          <CheckIcon className="w-5 h-5 text-[#16CA7C] mr-2" />
        ) : (
          <XMarkIcon className="w-5 h-5 text-[#EF2121] mr-2" />
        )}
        <span
          className={
            isValid
              ? "text-[#16CA7C] text-xs md:text-sm leading-[160%] font-bold"
              : "text-[#EF2121] text-xs md:text-sm leading-[160%] font-bold"
          }
        >
          {message}
        </span>
      </div>
    );
  };

  return (
    <div>
      {!isMobile && (
        <h4 className="text-[22px] font-bold leading-[160%] text-customGray mb-6">
          Change Password
        </h4>
      )}
      <form onSubmit={formik.handleSubmit}>
        <div className="border border-solid border-[#EBEFF3] rounded-md p-5 bg-white">
          <div className="xl:grid grid-cols-2 gap-5">
            <div className="mb-6">
              <h6 className="text-xs font-bold leading-[160%] text-[#869099] uppercase mb-2">
                Enter existing password
              </h6>
              <PasswordInputField
                type="password"
                name="oldPassword"
                classes="bg-white lg:h-[60px] flex items-center mx-auto w-full border border-solid border-strokeColor rounded-[10px] h-[48px] mb-[14px] xl:mb-0 relative"
                inputClasses="border-r-0 lg:text-lg text-sm font-medium text-customGray placeholder:text-[#869099] px-5 bg-transparent"
                value={formik.values.oldPassword}
                placeHolder="Enter existing password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.oldPassword && formik.errors.oldPassword ? (
                <div className="text-red-500 text-xs leading-[160%] font-bold">
                  {formik.errors.oldPassword}
                </div>
              ) : null}
            </div>
          </div>
          <div className="xl:grid grid-cols-2 gap-5">
            <div>
              <h6 className="text-xs font-bold leading-[160%] text-[#869099] uppercase mb-2">
                New password
              </h6>
              <PasswordInputField
                type="password"
                name="newPassword"
                classes="bg-white lg:h-[60px] flex items-center mx-auto w-full border border-solid border-strokeColor rounded-[10px] h-[48px] mb-[14px] xl:mb-0 relative"
                inputClasses="border-r-0 lg:text-lg text-sm font-medium text-customGray placeholder:text-[#869099] px-5 bg-transparent"
                value={formik.values.newPassword}
                placeHolder="New password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {
                <div className="my-3 md:my-0">
                  <ValidationMessage
                    isValid={formik.values.newPassword.length >= 8}
                    message="Password must be at least 8 characters long"
                  />
                  <ValidationMessage
                    isValid={/[A-Z]/.test(formik.values.newPassword)}
                    message="Password must contain at least one uppercase letter"
                  />
                  <ValidationMessage
                    isValid={/[0-9]/.test(formik.values.newPassword)}
                    message="Password must contain at least one number"
                  />
                  <ValidationMessage
                    isValid={/[@$!%*?&#]/.test(formik.values.newPassword)}
                    message="Password must contain at least one special character"
                  />
                </div>
              }
            </div>
            <div>
              <h6 className="text-xs font-bold leading-[160%] text-[#869099] uppercase mb-2">
                Confirm password
              </h6>
              <PasswordInputField
                type="password"
                name="confirmPassword"
                classes="bg-white lg:h-[60px] flex items-center mx-auto w-full border border-solid border-strokeColor rounded-[10px] h-[48px] mb-[14px] xl:mb-0 relative"
                inputClasses="border-r-0 lg:text-lg text-sm font-medium text-customGray placeholder:text-[#869099] px-5 bg-transparent"
                value={formik.values.confirmPassword}
                placeHolder="Confirm password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
              {formik.touched.confirmPassword &&
              formik.errors.confirmPassword ? (
                <div className="text-red-500 text-xs leading-[160%] font-bold">
                  {formik.errors.confirmPassword}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-5 justify-end mt-[30px]">
          <IconButton
            title="CANCEL"
            icon={XCircleIcon}
            iconClasses="w-6 h-6 text-[#485868]"
            classes="text-sm xl:text-base font-black leading-[160%] text-[#485868] uppercase  max-w-[161px] w-full flex items-center justify-center gap-[6px] bg-[#EBEFF3] h-10 xl:h-[58px] rounded-[10px]"
          />
          <IconButton
            title="Save"
            icon={CheckCircleIcon}
            iconClasses="w-6 h-6 text-white"
            classes="text-sm xl:text-base font-black leading-[160%] text-white uppercase max-w-[162px] md:max-w-[135px] w-full flex items-center justify-center gap-[6px] bg-primary h-10 xl:h-[58px] rounded-[10px]"
            onClick={formik.handleSubmit}
            buttonLoader={changePasswordLoader}
          />
        </div>
      </form>
    </div>
  );
};

export default AdminChangePassword;
